import { useEffect, useState } from 'react'
import { useTranslation }      from 'react-i18next'
import { useForm }             from 'react-hook-form'
import { listUsers, createUser, updateUser, deleteUser } from '@/api/resources'
import {
  Button, PageHeader, Badge, EmptyState, Spinner,
  FIELD_CLS, FIELD_AUTO_CLS, SearchInput, TableFooter,
} from '@/components/ui/primitives'
import { fetchAllPages } from '@/utils/fetchAll'
import { Plus, Pencil, Trash2, Eye, X, Users } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'

const PAGE_SIZE = 20
const ROLE_COLORS = { admin: 'red', teacher: 'blue', student: 'green', guest: 'amber' }

// ── Connected-provider badges ────────────────────────────────────────
function ProviderBadges({ user: u }) {
  const providers = [
    { key: 'telegram_connected', label: 'Telegram', variant: 'blue' },
    { key: 'google_connected', label: 'Google', variant: 'green' },
    { key: 'apple_connected', label: 'Apple', variant: 'gray' },
  ].filter(p => u[p.key])
  if (providers.length === 0) return <span className="secondary">—</span>
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {providers.map(p => <Badge key={p.key} variant={p.variant}>{p.label}</Badge>)}
    </div>
  )
}

function Avatar({ user: u }) {
  return u.avatar ? (
    <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
  ) : (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
      style={{ background: 'var(--surface-2, #eee)', color: 'var(--ink-3, #888)' }}>
      {(u.full_name || u.username || '?').slice(0, 1).toUpperCase()}
    </div>
  )
}

// ── SlideOver ─────────────────────────────────────────────────────────
function SlideOver({ open, title, onClose, children }) {
  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={onClose} />
      )}
      <div className={[
        'fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-50',
        'flex flex-col transition-transform duration-300 ease-in-out',
        open ? 'translate-x-0' : 'translate-x-full',
      ].join(' ')}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </>
  )
}

// ── UserView (read-only detail) ───────────────────────────────────────
function UserView({ user: u, t, onEdit, onClose }) {
  const fields = [
    { label: t('users.fullName'),   value: u.full_name || '—' },
    { label: t('users.username'),   value: `@${u.username}` },
    { label: t('users.email'),      value: u.email || '—' },
    { label: t('users.role'),       value: <Badge variant={ROLE_COLORS[u.role] ?? 'gray'}>{t(`users.roles.${u.role}`, u.role)}</Badge> },
    { label: t('users.connectedAccounts', 'Bog\'langan hisoblar'), value: <ProviderBadges user={u} /> },
    { label: t('common.createdAt'), value: u.date_joined ? new Date(u.date_joined).toLocaleString() : '—' },
  ]
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 pb-2">
        <Avatar user={u} />
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">{u.full_name || u.username}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">@{u.username}</p>
        </div>
      </div>
      {fields.map(({ label, value }) => (
        <div key={label} className="flex flex-col gap-0.5">
          <p className="text-xs font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-sm text-gray-800 dark:text-slate-200">{value}</p>
        </div>
      ))}
      <div className="flex gap-2 pt-4 border-t border-gray-100 dark:border-slate-800">
        <Button variant="secondary" onClick={onClose} className="flex-1">{t('common.close', 'Yopish')}</Button>
        <Button variant="primary" onClick={onEdit} className="flex-1" icon={<Pencil size={14} />}>{t('common.edit')}</Button>
      </div>
    </div>
  )
}

// ── UserForm ──────────────────────────────────────────────────────────
function UserForm({ initial, onSaved, onCancel, t }) {
  const isEdit = !!initial
  const { register, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      username:  initial?.username  || '',
      full_name: initial?.full_name || '',
      email:     initial?.email     || '',
      role:      initial?.role      || 'teacher',
      password:  '',
    }
  })
  const [saveError, setSaveError] = useState(null)

  async function onSubmit(data) {
    setSaveError(null)
    try {
      const payload = { ...data }
      if (!payload.password) delete payload.password
      if (isEdit) { delete payload.username; await updateUser(initial.id, payload) }
      else        await createUser(payload)
      onSaved()
    } catch (err) {
      setSaveError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('users.fullName')} <span className="text-red-500">*</span>
        </label>
        <input {...register('full_name', { required: true })} className={FIELD_CLS} />
        {errors.full_name && <p className="text-xs text-red-500">{t('common.required')}</p>}
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('users.username')} <span className="text-red-500">*</span>
        </label>
        <input {...register('username', { required: true })} autoComplete="username" disabled={isEdit} className={FIELD_CLS} />
        {errors.username && <p className="text-xs text-red-500">{t('common.required')}</p>}
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('users.email')}
        </label>
        <input {...register('email')} type="email" className={FIELD_CLS} />
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('users.role')} <span className="text-red-500">*</span>
        </label>
        <select {...register('role', { required: true })} className={FIELD_CLS}>
          {['admin', 'teacher', 'student'].map(r => (
            <option key={r} value={r}>{t(`users.roles.${r}`, r)}</option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('users.password')}
          {!isEdit && <span className="text-red-500 ml-0.5">*</span>}
          {isEdit  && <span className="text-xs text-gray-400 ml-1">({t('users.passwordHint', 'o\'zgartirmasangiz bo\'sh qoldiring')})</span>}
        </label>
        <input
          {...register('password', isEdit ? {} : { required: true, minLength: 8 })}
          type="password" autoComplete="new-password"
          placeholder={isEdit ? '••••••••' : ''}
          className={FIELD_CLS}
        />
        {errors.password && <p className="text-xs text-red-500">{t('users.passwordMin', 'Kamida 8 ta belgi')}</p>}
      </div>

      {saveError && (
        <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg break-all">
          {saveError}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-slate-800">
        <Button variant="secondary" type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={isSubmitting}>
          {isEdit ? t('common.save') : t('users.createUser')}
        </Button>
      </div>
    </form>
  )
}


// ── Page ──────────────────────────────────────────────────────────────
export default function UsersPage() {
  const { t } = useTranslation()

  const [users,   setUsers]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [search,  setSearch]  = useState('')
  const [role,    setRole]    = useState('')
  const [page,    setPage]    = useState(1)

  // panel: null | { mode: 'view'|'create'|'edit', user?: object }
  const [panel,   setPanel]   = useState(null)
  const [confirm, setConfirm] = useState(null)

  function fetchUsers() {
    setLoading(true)
    fetchAllPages(listUsers, { search: search || undefined, role: role || undefined })
      .then(data => setUsers(role === 'guest' ? data : data.filter(u => u.role !== 'guest')))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchUsers() }, [search, role])
  useEffect(() => { setPage(1) }, [search, role])

  async function handleConfirmDelete() {
    await deleteUser(confirm)
    setConfirm(null)
    fetchUsers()
  }

  const totalPages = Math.ceil(users.length / PAGE_SIZE)
  const paginated  = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const panelTitle =
    panel?.mode === 'view'   ? t('users.viewUser',   'Foydalanuvchi')  :
    panel?.mode === 'edit'   ? t('users.editUser',   'Tahrirlash')     :
    panel?.mode === 'create' ? t('users.createUser', 'Foydalanuvchi qo\'shish') : ''

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('users.title', 'Foydalanuvchilar')}
        subtitle={`${users.length} ${t('common.total', 'jami').toLowerCase()}`}
        actions={
          <Button icon={<Plus size={15} />} onClick={() => setPanel({ mode: 'create' })}>
            {t('users.createUser', 'Qo\'shish')}
          </Button>
        }
      />

      {/* Search + filter row */}
      <div className="flex items-center gap-3">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search', 'Qidirish...')}
        />
        <select value={role} onChange={e => setRole(e.target.value)} className={FIELD_AUTO_CLS}>
          <option value="">{t('common.filter', 'Filtr')}: {t('users.role', 'Rol')}</option>
          {['admin', 'teacher', 'student', 'guest'].map(r => (
            <option key={r} value={r}>{t(`users.roles.${r}`, r)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : error ? (
          <div className="p-6 text-red-600 text-sm">{error}</div>
        ) : users.length === 0 ? (
          <EmptyState icon={<Users size={48} />} title={t('common.noData', 'Ma\'lumot topilmadi')}
            action={<Button icon={<Plus size={15} />} onClick={() => setPanel({ mode: 'create' })}>{t('users.createUser')}</Button>}
          />
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr className="">
                  <th className="">{t('users.fullName', 'Ism')}</th>
                  <th className="">{t('users.username', 'Login')}</th>
                  <th className="">{t('users.role', 'Rol')}</th>
                  <th className="">{t('users.connectedAccounts', 'Bog\'langan hisoblar')}</th>
                  <th className="">{t('common.createdAt', 'Sana')}</th>
                  <th className="">{t('common.actions', 'Amallar')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(u => (
                  <tr key={u.id} className="">
                    <td className="">
                      <div className="flex items-center gap-2.5">
                        <Avatar user={u} />
                        <span className="primary">{u.full_name || '—'}</span>
                      </div>
                    </td>
                    <td className=""><span className="secondary font-mono">@{u.username}</span></td>
                    <td className="">
                      <Badge variant={ROLE_COLORS[u.role] ?? 'gray'}>{t(`users.roles.${u.role}`, u.role)}</Badge>
                    </td>
                    <td className=""><ProviderBadges user={u} /></td>
                    <td className=""><span className="secondary">{u.date_joined ? new Date(u.date_joined).toLocaleDateString() : '—'}</span></td>
                    <td className="tr">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setPanel({ mode: 'view', user: u })}
                          className="tbl-btn tbl-go"
                          title={t('common.view', 'Ko\'rish')}
                        ><Eye size={15} /></button>
                        <button
                          onClick={() => setPanel({ mode: 'edit', user: u })}
                          className="tbl-btn tbl-edit"
                          title={t('common.edit', 'Tahrirlash')}
                        ><Pencil size={15} /></button>
                        <button
                          onClick={() => setConfirm(u.id)}
                          className="tbl-btn tbl-danger"
                          title={t('common.delete', 'O\'chirish')}
                        ><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TableFooter page={page} totalPages={totalPages} totalCount={users.length} onPageChange={setPage} />
          </>
        )}
      </div>

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* SlideOver panel */}
      <SlideOver
        open={!!panel}
        title={panelTitle}
        onClose={() => setPanel(null)}
      >
        {panel?.mode === 'view' && panel.user && (
          <UserView
            user={panel.user}
            t={t}
            onEdit={() => setPanel({ mode: 'edit', user: panel.user })}
            onClose={() => setPanel(null)}
          />
        )}
        {(panel?.mode === 'create' || panel?.mode === 'edit') && (
          <UserForm
            key={panel?.user?.id ?? 'new'}
            initial={panel?.mode === 'edit' ? panel.user : null}
            t={t}
            onSaved={() => { setPanel(null); fetchUsers() }}
            onCancel={() => setPanel(null)}
          />
        )}
      </SlideOver>
    </div>
  )
}
